import { z } from 'zod';

import {
  CONNECTION_OWNER_TYPES,
  CONNECTION_PROVIDERS,
  CONNECTION_STATUSES,
} from '../connection.js';
import { TOOL_CLASSIFICATIONS, TOOL_SIDE_EFFECTS } from '../tool-capability.js';

/**
 * Connections as the SPA sees them - API-011.
 *
 * `/v1/connections` rather than `/v1/integrations/...`: ADR-030 keeps resources
 * plural and flat, the way `/v1/employees` and `/v1/conversations` are, and the
 * resource here is a connection. "Integrations" is what the screen is called,
 * which is a different question from what the thing is.
 *
 * **No connect flow here.** These three routes are CRUD over rows that already
 * exist; asking a provider for a connect token is INT-006 and needs credentials
 * this batch deliberately does not have. So nothing in this file talks to
 * Pipedream, and everything in it can be tested with no network.
 *
 * The vocabularies are imported rather than restated, for the reason
 * `http/employee.ts` gives: two definitions drift the first time only one is
 * edited, and the drift is silent.
 */

/**
 * The database puts no ceiling on these columns. The API does - an unbounded
 * string from a user-supplied field is an input-validation gap even when the
 * column would hold it. Deliberately stricter than the schema, never looser.
 */
export const CONNECTION_APP_KEY_MAX = 120;
export const CONNECTION_DISPLAY_NAME_MAX = 120;

export const connectionSchema = z
  .object({
    id: z.uuid(),
    ownerType: z.enum(CONNECTION_OWNER_TYPES),
    ownerId: z.uuid().optional(),
    appKey: z.string(),
    provider: z.enum(CONNECTION_PROVIDERS),
    /**
     * Published because the Integrations screen needs something to show beside
     * a second account for the same app, and because a person disconnecting one
     * of two HubSpots has to be able to tell them apart. It identifies a
     * connection at the provider and is not a credential.
     */
    providerConnectionId: z.string().optional(),
    displayName: z.string(),
    externalAccountId: z.string().optional(),
    scopes: z.array(z.string()).readonly(),
    status: z.enum(CONNECTION_STATUSES),
    lastVerifiedAt: z.string().optional(),
    createdAt: z.string(),
  })
  .readonly();

export type ConnectionSummary = z.infer<typeof connectionSchema>;

export const connectionListResponseSchema = z
  .object({ connections: z.array(connectionSchema).readonly() })
  .readonly();

export type ConnectionListResponse = z.infer<typeof connectionListResponseSchema>;

export const connectionResponseSchema = z.object({ connection: connectionSchema }).readonly();

export type ConnectionResponse = z.infer<typeof connectionResponseSchema>;

/**
 * Opens a connection, before anybody has been to the provider.
 *
 * Carries neither `status` nor `providerConnectionId`: both are the server's to
 * decide, and a row created `AVAILABLE` with no provider id would violate
 * `connection_available_has_provider_id`. A caller able to ask for either is a
 * caller able to make that mistake - the same argument `createEmployeeRequest`
 * makes about type and status.
 *
 * `ownerType` and `ownerId` are absent too. Ownership rules are INT-021 and
 * INT-022, both P1; until they exist a connection belongs to the workspace that
 * created it, which the route sets. Publishing the field first would invite a
 * client to choose, and taking it away again later is a breaking change.
 */
export const createConnectionRequestSchema = z
  .object({
    appKey: z.string().trim().min(1).max(CONNECTION_APP_KEY_MAX),
    provider: z.enum(CONNECTION_PROVIDERS),
    /** Falls back to the app key, so a connection always has something to show. */
    displayName: z.string().trim().min(1).max(CONNECTION_DISPLAY_NAME_MAX).optional(),
  })
  .readonly();

export type CreateConnectionRequest = z.infer<typeof createConnectionRequestSchema>;

/**
 * What the browser needs to finish connecting - INT-006.
 *
 * `connectUrl` is Pipedream's own address with the app appended, not one we
 * assemble: the provider owns that address and has changed it before. The token
 * is published beside it because the SDK flow takes the token rather than the
 * link, and a client may use either.
 *
 * **Short-lived by construction.** `expiresAt` is here so the SPA can tell the
 * difference between "the user took too long" and "something is broken", which
 * are different messages.
 */
export const connectSessionResponseSchema = z
  .object({
    connection: connectionSchema,
    token: z.string(),
    expiresAt: z.string(),
    connectUrl: z.url(),
  })
  .readonly();

export type ConnectSessionResponse = z.infer<typeof connectSessionResponseSchema>;

/**
 * Asking the server to check whether the connection completed - INT-006.
 *
 * There is no webhook yet, so the SPA asks after the Connect window closes.
 * The answer is the connection as it now stands: `AVAILABLE` if the provider
 * has an account for it, still `PENDING` if not. Deliberately not an error when
 * nothing has changed - a person who cancelled the flow has not done anything
 * wrong.
 */
export const connectionSyncResponseSchema = z
  .object({
    connection: connectionSchema,
    /** True when this call is what moved it to `AVAILABLE`. */
    changed: z.boolean(),
  })
  .readonly();

export type ConnectionSyncResponse = z.infer<typeof connectionSyncResponseSchema>;

/**
 * How many tools one request may switch off - COW-493.
 *
 * A ceiling on the *request*, not on the table. The largest app measured
 * exposes 18 tools; two hundred leaves room for one an order of magnitude
 * bigger without letting an unbounded array through the parser, which is what
 * `CONNECTION_APP_KEY_MAX` above exists for one field down.
 */
export const CONNECTION_DISABLED_TOOLS_MAX = 200;
export const TOOL_NAME_MAX = 200;

/**
 * One of an app's tools, as the connection's picker sees it - AGT-006.
 *
 * `enabled` is computed rather than stored: `connection_tool` holds only what
 * somebody switched **off**, so a tool nobody has touched is enabled by the
 * absence of a row. The screen should not have to know that.
 *
 * `sideEffect` and `classification` are published because the choice is
 * otherwise uninformed - §14.3's rule that an approval UI must show what will
 * actually happen applies just as well to granting the permission in the first
 * place. `UNCLASSIFIED` means the provider said nothing about side effects,
 * which is a different statement from "it is a write".
 *
 * `inputSchema` is deliberately withheld. It is somebody else's JSON Schema,
 * it is the largest field in the mirror, and no screen has a use for it -
 * `GET /v1/tools` withholds the same field for the same reason.
 */
export const connectionToolSchema = z
  .object({
    toolName: z.string(),
    description: z.string().optional(),
    sideEffect: z.enum(TOOL_SIDE_EFFECTS),
    classification: z.enum(TOOL_CLASSIFICATIONS),
    enabled: z.boolean(),
  })
  .readonly();

export type ConnectionToolSummary = z.infer<typeof connectionToolSchema>;

export const connectionToolListResponseSchema = z
  .object({
    tools: z.array(connectionToolSchema).readonly(),
    /**
     * True when the mirror has no tools for this app at all.
     *
     * An empty `tools` array is otherwise ambiguous - an app with nothing to
     * offer looks the same as one whose listing has not run - and the screen
     * needs to tell a person "nothing to choose yet" rather than "no tools".
     * `scripts/sync-tools.sh` is what fills it.
     */
    mirrorEmpty: z.boolean(),
  })
  .readonly();

export type ConnectionToolListResponse = z.infer<typeof connectionToolListResponseSchema>;

/**
 * Replacing the switched-off set - COW-493.
 *
 * **The whole set, not a delta.** A partial update would need the client to
 * know what it did not send, and two screens open at once would each silently
 * undo the other. Sending the whole set makes the last writer win, which is at
 * least a rule somebody can predict.
 *
 * `POST` and not `PUT`, although the semantics are a replacement: nothing in
 * this API uses `PUT` and the SPA's client does not have the verb, so adding
 * one buys nothing that `POST /:id/status` does not already do. An earlier
 * version of this note also claimed the gateway would need changing; it would
 * not - `PUT` is already routed on `/{proxy+}` and already in
 * `corsPreflight.allowMethods`. A review checked in seconds what this
 * asserted.
 */
export const setConnectionToolsRequestSchema = z
  .object({
    disabled: z
      .array(z.string().trim().min(1).max(TOOL_NAME_MAX))
      .max(CONNECTION_DISABLED_TOOLS_MAX)
      .readonly(),
  })
  .readonly();

export type SetConnectionToolsRequest = z.infer<typeof setConnectionToolsRequestSchema>;
